import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircle, ChevronRight, ShieldAlert } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Dashboard widget — surfaces upcoming parties (next 14 days) that still have
 * guests who haven't submitted the allergy / consent form.
 * Hidden when there's nothing pending (keeps the overview clean).
 */
export const PendingAllergyFormsWidget = ({ daysAhead = 14 }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [totalForms, setTotalForms] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/pending-allergy-forms`, {
      params: { days_ahead: daysAhead },
      withCredentials: true,
    })
      .then((res) => {
        setItems(res.data?.items || []);
        setTotalForms(res.data?.total_pending_forms || 0);
      })
      .catch(() => { /* silent — widget hides itself */ })
      .finally(() => setLoaded(true));
  }, [daysAhead]);

  if (!loaded || totalForms === 0) return null;

  const partyCount = items.length;
  const copy = `${totalForms} guest${totalForms === 1 ? '' : 's'} across ${partyCount} part${partyCount === 1 ? 'y' : 'ies'} still need${totalForms === 1 ? 's' : ''} to complete the allergy form.`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="bg-white rounded-2xl border-2 border-[#E5484D]/60 p-5"
      data-testid="pending-allergy-widget"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-[#E5484D]/12 flex items-center justify-center flex-shrink-0">
          <ShieldAlert className="w-5 h-5 text-[#E5484D]" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-bold text-[#1E1E24]">Pending allergy forms</h3>
            <span className="text-[10px] bg-[#E5484D]/15 text-[#E5484D] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
              Next {daysAhead} days
            </span>
          </div>
          <p className="text-xs text-[#6B6B7A] mt-0.5">{copy}</p>
        </div>
      </div>

      <div className="divide-y divide-[#F0EDE6] rounded-xl border border-[#E8E5DF] overflow-hidden">
        {items.slice(0, 6).map((it) => (
          <button
            key={it.invitation_id}
            type="button"
            onClick={() => navigate(`/crm/invitations/${it.invitation_id}`)}
            className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-[#FFFDF8] transition"
            data-testid={`pending-allergy-row-${it.invitation_id}`}
          >
            <div className="flex items-center gap-2 min-w-0">
              <AlertCircle className="w-3.5 h-3.5 text-[#E5484D] flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#1E1E24] truncate">
                  {it.child_name}'s party — {it.party_date}
                </p>
                <p className="text-[11px] text-[#6B6B7A] truncate">
                  Host: {it.owner_name || '—'} · {it.party_time || 'TBD'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <span className="text-xs font-bold text-[#E5484D]">
                {it.pending_count}/{it.total_yes} pending
              </span>
              <ChevronRight className="w-4 h-4 text-[#6B6B7A]" />
            </div>
          </button>
        ))}
      </div>

      {items.length > 6 && (
        <p className="text-[11px] text-[#6B6B7A] mt-2 text-center">
          +{items.length - 6} more · open CRM → Parties → Invitations for the full list
        </p>
      )}
    </motion.div>
  );
};

export default PendingAllergyFormsWidget;
